import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import Section from "@/pages/settings/components/sections/Section";
import { SettingItemLayoutProps } from "@/pages/settings/components/settingsItems/SettingItemLayout";
import {
  useAccountSettingByKey,
  useUpdateAccountSettingByKey
} from "@/entities/settings/hooks";
import { isUnsetAccountSettingError } from "@/entities/settings/accountSettingDefaults";

const TRANSLATION_KEY = "settings.contracts.general";
const FX_REFRESH_KEY = `${TRANSLATION_KEY}.fxRates.refreshFrequency`;

const FX_REFRESH_FREQUENCY_SETTING_KEY = "contracts.fx_refresh_frequency";

enum FxRefreshFrequency {
  NEVER = "never",
  DAILY = "daily",
  MONTHLY = "monthly",
  QUARTERLY = "quarterly"
}

// What the backend acts on when the account never stored a frequency.
const DEFAULT_FX_REFRESH_FREQUENCY = FxRefreshFrequency.MONTHLY;

const isFxRefreshFrequency = (value: unknown): value is FxRefreshFrequency =>
  Object.values(FxRefreshFrequency).includes(value as FxRefreshFrequency);

// The stored frequency, or the default when the settings API confirms the key
// is unset (404) or holds no value. Undefined while the read is in flight or
// after it failed for any other reason: a failed read says nothing about what
// is stored, so it must not show the default as though it were, and the
// select stays locked rather than let an admin overwrite a value they cannot see.
const useFxRefreshFrequency = (): {
  value: FxRefreshFrequency | undefined;
  isLoading: boolean;
  isLoadError: boolean;
} => {
  const { data, isError, error } = useAccountSettingByKey(
    FX_REFRESH_FREQUENCY_SETTING_KEY
  );
  if (data !== undefined) {
    const stored = data.value;
    if (stored === null || stored === undefined) {
      return {
        value: DEFAULT_FX_REFRESH_FREQUENCY,
        isLoading: false,
        isLoadError: false
      };
    }
    // A stored value this client does not know is as unreadable as a failed
    // read: showing it as empty would let an admin overwrite it unseen.
    return isFxRefreshFrequency(stored)
      ? { value: stored, isLoading: false, isLoadError: false }
      : { value: undefined, isLoading: false, isLoadError: true };
  }
  if (isError && isUnsetAccountSettingError(error)) {
    return {
      value: DEFAULT_FX_REFRESH_FREQUENCY,
      isLoading: false,
      isLoadError: false
    };
  }
  return { value: undefined, isLoading: !isError, isLoadError: isError };
};

// Account-wide contract settings. The settings route itself is admin-only,
// so nothing here gates on a permission of its own.
export const GeneralTab: React.FC = () => {
  const { t } = useTranslation();
  const {
    value: storedFrequency,
    isLoading,
    isLoadError
  } = useFxRefreshFrequency();
  const updateSettingByKey = useUpdateAccountSettingByKey();
  // The choice being saved, shown until the store catches up so the select
  // does not snap back to the old value while the request is in flight.
  const [pendingFrequency, setPendingFrequency] =
    useState<FxRefreshFrequency | null>(null);

  const handleSelection = (value: string | number) => {
    if (!isFxRefreshFrequency(value)) return;
    // One write at a time: two in flight can land out of order and leave the
    // store on the older choice.
    if (pendingFrequency !== null) return;
    if (value === storedFrequency) return;
    setPendingFrequency(value);
    updateSettingByKey
      .mutateAsync({
        pathParams: { settingKey: FX_REFRESH_FREQUENCY_SETTING_KEY },
        data: { value }
      })
      .then(
        () => setPendingFrequency(null),
        () => {
          setPendingFrequency(null);
          toast.error(t(`${FX_REFRESH_KEY}.saveError`));
        }
      );
  };

  const options = Object.values(FxRefreshFrequency).map((frequency) => ({
    id: frequency,
    displayName: t(`${FX_REFRESH_KEY}.options.${frequency}`)
  }));

  return (
    <Section
      title={t(`${TRANSLATION_KEY}.fxRates.title`)}
      subTitle={t(`${TRANSLATION_KEY}.fxRates.subTitle`)}
      items={
        [
          {
            title: t(`${FX_REFRESH_KEY}.title`),
            subTitle: t(
              `${FX_REFRESH_KEY}.${isLoadError ? "loadError" : "subTitle"}`
            ),
            actionType: "singleSelect",
            actionProps: {
              fieldName: "fxRefreshFrequency",
              options,
              selectedValue: pendingFrequency ?? storedFrequency ?? "",
              onSelection: handleSelection,
              disabled: isLoading || isLoadError || pendingFrequency !== null,
              formatter: (value: string) =>
                isFxRefreshFrequency(value)
                  ? t(`${FX_REFRESH_KEY}.options.${value}`)
                  : value
            }
          }
        ] as SettingItemLayoutProps[]
      }
    />
  );
};
